import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useViewedTypeStore, useIdStore, useRelatedContactsStore, usePresentationModeStore } from "../../stores/selectedToViewStore";
import LoadingIndicator from "../../shared/UIParts/LoadingIndicator";
import ErrorAlert from "../../shared/UIParts/ErrorAlert";
import { useActiveUserOrFamilyData } from "../../hooks/consulting/useActiveUserOrFamilyData";
import { useFamilies } from "../../hooks/crm/useFamilies";
import UserFamilyListItem from "../../shared/UIParts/UserFamilyListItem";

export const PresentationModeList: React.FC = () => {
  const viewedType = useViewedTypeStore((state) => state.viewedType);
  const setViewedType = useViewedTypeStore((state) => state.setViewedType);
  const id = useIdStore((state) => state.id);
  const setId = useIdStore((state) => state.setId);
  const relatedContacts = useRelatedContactsStore((state) => state.relatedContacts);

  // Frozen presentation mode state
  const presentationRelatedContacts = usePresentationModeStore((state) => state.presentationRelatedContacts);
  const presentationInitialId = usePresentationModeStore((state) => state.presentationInitialId);
  const presentationInitialType = usePresentationModeStore((state) => state.presentationInitialType);

  const { contacts, familyMembers, isLoading, isError } = useActiveUserOrFamilyData();
  const { data: families } = useFamilies();

  // במצב תצוגה - להשתמש ברשימה הקפואה בלבד!
  const allDisplayedContacts = useMemo(() => {
    if (presentationRelatedContacts.length > 0) {
      return presentationRelatedContacts;
    }

    // אם אין רשימה קפואה (לא אמור לקרות), נשתמש ברשימה הרגילה
    const filteredRelated = relatedContacts.filter(
      (related) => !contacts.find((c) => c.id === related.id)
    );
    return [...contacts, ...filteredRelated];
  }, [contacts, relatedContacts, presentationRelatedContacts]);

  if (isLoading) return <LoadingIndicator />;
  if (isError) return <ErrorAlert message="Failed to load data" />;

  // במצב תצוגה - להשתמש ב-ID והטיפוס הקפואים כדי לקבוע איזו משפחה להציג
  const displayId = presentationInitialId ?? id;
  const displayType = presentationInitialType ?? viewedType;

  // אם אין ID נבחר, לא מציגים כלום
  if (!displayId || !displayType) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          בחר איש קשר או משפחה מהרשימה
        </Typography>
      </Box>
    );
  }

  // מציאת המשפחה אם זה displayType === "FAMILY" - משתמש ב-displayId הקפוא!
  const family = displayType === "FAMILY"
    ? families?.find(f => f.id === displayId)
    : null;

  // עדכון ה-store בלבד - במצב תצוגה ה-URL נשאר קפוא
  const handleSelect = (isContact: boolean, itemId: number) => {
    const newType = isContact ? "USER" : "FAMILY";

    // עדכון רק את ה-store, ללא שינוי URL
    // במצב תצוגה ה-URL חייב להישאר קפוא
    setViewedType(newType);
    setId(itemId);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0, overflowX: "hidden" }}>
      {/* כרטיס משפחה אם זה משפחה - מציג לפי displayType הקפוא */}
      {displayType === "FAMILY" && family && (
        <UserFamilyListItem
          item={family}
          isSelected={family.id === id}
          onSelect={handleSelect}
        />
      )}

      {/* רשימת אנשי קשר - רשימה קפואה! */}
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {allDisplayedContacts.map((contact) => (
          <UserFamilyListItem
            key={contact.id}
            item={contact}
            isSelected={contact.id === id}
            onSelect={handleSelect}
          />
        ))}
      </Box>
    </Box>
  );
};
